import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Search, ShoppingCart, Bell, LogOut, User, LayoutDashboard, MessageSquareWarning, Instagram, Facebook } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useCart } from '@/lib/cart';
import { useCompany } from '@/lib/company';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { routeForNotification } from '@/lib/notificationRoutes';
import { NAV } from '@/constants/testIds';
import { DashboardNav } from '@/components/DashboardNav';
import { PersistentShortcutBar } from '@/components/PersistentShortcutBar';
import { Logo } from '@/components/Logo';

const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState('');
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get('/notifications').then(({ data }) => setNotifs(data.items || [])).catch(() => {});
  }, [user, loc.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const openNotification = async (n) => {
    const target = routeForNotification(n, user?.role);
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch { /* silent */ }
      setNotifs((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
    }
    if (target) nav(target);
    else nav('/notificaciones');
  };

  const submit = (e) => {
    e.preventDefault();
    const sp = new URLSearchParams();
    if (q.trim()) sp.set('q', q.trim());
    nav(`/catalogo?${sp.toString()}`);
  };

  const dashboardPath =
    user?.role === 'admin' ? '/admin' :
    user?.role === 'logistics' ? '/logistica' :
    user?.role === 'seller' ? '/vendedor' : '/mis-pedidos';

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
        {/* Row 1 — logo + right-side icons/buttons */}
        <div className="flex h-[72px] sm:h-20 items-center gap-3">
          <Logo size="md" className="mr-1" />

          {/* Search — desktop only in the header row */}
          <form onSubmit={submit} className="hidden sm:block flex-1 max-w-2xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid={NAV.searchInput}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar repuestos, marca, vehículo o vendedor..."
                className="pl-10 rounded-none border-slate-300 focus-visible:ring-blue-600 h-10"
              />
              <button
                data-testid={NAV.searchBtn}
                type="submit"
                className="absolute right-0 top-0 h-10 px-4 bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
              >
                Buscar
              </button>
            </div>
          </form>

          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <Link to="/carrito" data-testid={NAV.cartBtn} className="relative inline-flex h-10 w-10 items-center justify-center hover:bg-slate-100">
              <ShoppingCart className="h-5 w-5 text-slate-700" />
              {count > 0 && (
                <Badge className="absolute -right-1 -top-1 h-5 min-w-[20px] rounded-none bg-orange-600 text-white px-1 text-[10px]">
                  {count}
                </Badge>
              )}
            </Link>

            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={NAV.notifBtn} className="relative inline-flex h-10 w-10 items-center justify-center hover:bg-slate-100">
                    <Bell className="h-5 w-5 text-slate-700" />
                    {unread > 0 && (
                      <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-orange-600" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 rounded-none">
                  <DropdownMenuLabel>Notificaciones</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifs.length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-slate-500">Sin notificaciones</div>
                  )}
                  {notifs.slice(0, 8).map((n) => (
                    <button
                      key={n.id}
                      type="button"
                      data-testid={`notif-header-row-${n.id}`}
                      onClick={() => openNotification(n)}
                      className={`w-full text-left px-3 py-2 text-sm border-b border-slate-100 last:border-0 hover:bg-slate-50 ${n.read ? '' : 'bg-blue-50/40'}`}
                    >
                      <div className="font-semibold text-slate-900 truncate">{n.title}</div>
                      <div className="text-slate-600 text-xs truncate">{n.body}</div>
                    </button>
                  ))}
                  <DropdownMenuSeparator />
                  <Link
                    to="/notificaciones"
                    data-testid="notif-see-all-link"
                    className="block px-3 py-2 text-center text-xs font-bold uppercase tracking-wider text-blue-700 hover:bg-slate-50"
                  >
                    Ver todas
                  </Link>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={NAV.userMenu} className="inline-flex items-center gap-2 h-10 px-2 sm:px-3 hover:bg-slate-100">
                    <div className="h-7 w-7 bg-slate-900 text-white flex items-center justify-center text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <span className="hidden md:inline text-sm font-medium text-slate-900">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rounded-none">
                  <DropdownMenuLabel>
                    <div className="flex items-center gap-2">
                      <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] ${
                        user.role === 'admin' ? 'bg-rose-600 text-white'
                        : user.role === 'seller' ? 'bg-orange-500 text-slate-900'
                        : 'bg-blue-600 text-white'
                      }`}>
                        {user.role === 'admin' ? 'Admin' : user.role === 'seller' ? 'Vendedor' : user.role === 'logistics' ? 'Logística' : 'Comprador'}
                      </span>
                    </div>
                    <div className="font-semibold text-slate-900 mt-1 truncate">{user.name || user.email}</div>
                    <div className="text-[11px] text-slate-500 truncate">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid={NAV.dashboardLink} onClick={() => nav(dashboardPath)}>
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Mi panel
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="nav-profile-link" onClick={() => nav('/perfil')}>
                    <User className="mr-2 h-4 w-4" /> Mi cuenta y contraseña
                  </DropdownMenuItem>
                  {user.role === 'buyer' && (
                    <DropdownMenuItem onClick={() => nav('/mis-pedidos')}>
                      <ShoppingCart className="mr-2 h-4 w-4" /> Mis pedidos
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="nav-mypqr-link" onClick={() => nav('/mis-pqr')}>
                    <MessageSquareWarning className="mr-2 h-4 w-4" /> Mis PQR
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid={NAV.logoutBtn} onClick={async () => { await logout(); nav('/'); }}>
                    <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                {/* Mobile — compact user icon that toggles a small menu */}
                <Link to="/login" className="sm:hidden inline-flex h-10 w-10 items-center justify-center hover:bg-slate-100" data-testid="nav-mobile-login">
                  <User className="h-5 w-5 text-slate-700" />
                </Link>
                <div className="hidden sm:flex items-center gap-1">
                  <Link to="/login">
                    <Button type="button" data-testid={NAV.loginBtn} variant="ghost" className="rounded-none">Ingresar</Button>
                  </Link>
                  <Link to="/registro">
                    <Button type="button" data-testid={NAV.registerBtn} className="rounded-none bg-slate-900 hover:bg-slate-800">Crear cuenta</Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Row 2 — mobile only: full-width search */}
        <form onSubmit={submit} className="sm:hidden pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid={NAV.searchInput + '-mobile'}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar repuestos, marca, vehículo o vendedor..."
              className="pl-10 pr-16 rounded-none border-slate-300 focus-visible:ring-blue-600 h-10"
            />
            <button
              type="submit"
              className="absolute right-0 top-0 h-10 px-3 bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"
              aria-label="Buscar"
            >
              <Search className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </header>
  );
};

/**
 * Social icons from simpleicons.org (brand SVGs). `#` links are placeholders
 * until the client provides the real handles.
 */
/**
 * Payment method logos. International brands come from simple-icons (verified
 * reachable). Colombian methods use tipographic badges with their official
 * brand color so the row stays consistent without relying on unstable image
 * hosts (Wikipedia hotlinks fail cross-origin).
 */
const PAYMENT_METHODS = [
  { label: 'Visa',           kind: 'icon', slug: 'visa',            color: '1A1F71' },
  { label: 'Mastercard',     kind: 'icon', slug: 'mastercard',      color: 'EB001B' },
  { label: 'American Express', kind: 'icon', slug: 'americanexpress', color: '2E77BC' },
  { label: 'PSE',            kind: 'text', text: 'PSE',        bg: '#02377A', fg: '#ffffff' },
  { label: 'Nequi',          kind: 'text', text: 'nequi',      bg: '#FF0080', fg: '#ffffff' },
  { label: 'Bancolombia',    kind: 'text', text: 'Bancolombia',bg: '#FDDA24', fg: '#0F3D8F' },
  { label: 'Daviplata',      kind: 'text', text: 'Daviplata',  bg: '#ED1C24', fg: '#ffffff' },
];

// SocialIcon: renders local SVG for each social brand without depending
// on external CDNs (cdn.simpleicons.org intermittently returns 0x0
// images due to bot filtering).
const SocialIcon = ({ slug }) => {
  const cls = 'h-4 w-4 text-white';
  switch (slug) {
    case 'instagram':
      return <Instagram className={cls} strokeWidth={2} />;
    case 'facebook':
      return <Facebook className={cls} strokeWidth={2} />;
    case 'tiktok':
      return (
        <svg viewBox="0 0 24 24" className={cls} fill="currentColor" aria-hidden="true">
          <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      );
    default:
      return <Instagram className={cls} strokeWidth={2} />;
  }
};

const PaymentBadge = ({ m }) => {
  const testid = `footer-pay-${m.label.toLowerCase().replace(/\s+/g, '-')}`;
  if (m.kind === 'icon') {
    return (
      <span
        title={m.label}
        data-testid={testid}
        className="bg-white rounded px-2 py-1 h-7 inline-flex items-center justify-center min-w-[42px]"
      >
        <img
          src={`https://cdn.simpleicons.org/${m.slug}/${m.color}`}
          alt={m.label}
          className="h-4 max-w-[42px] object-contain"
          loading="lazy"
        />
      </span>
    );
  }
  return (
    <span
      title={m.label}
      data-testid={testid}
      className="rounded px-2 h-7 inline-flex items-center justify-center text-[10px] font-black tracking-wide"
      style={{ backgroundColor: m.bg, color: m.fg, letterSpacing: '0.02em' }}
    >
      {m.text}
    </span>
  );
};

const Footer = () => {
  const company = useCompany();
  return (  <footer className="border-t border-slate-200 bg-slate-900 text-slate-300 mt-16">
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-6 gap-8 text-sm">
      <div className="col-span-2">
        <Logo variant="dark" size="md" />
        <p className="mt-4 text-slate-400 leading-relaxed text-xs max-w-xs">
          El marketplace #1 de repuestos automotrices en Colombia. Miles de productos originales
          y genéricos con vendedores verificados, pagos seguros y envíos a todo el país.
        </p>
        {/* Contact block (canonical source: settings.platform) */}
        <ul className="mt-5 space-y-1.5 text-xs text-slate-400" data-testid="footer-contact">
          {company.company_address && (
            <li data-testid="footer-address" className="leading-snug">{company.company_address}</li>
          )}
          {company.company_phone && (
            <li>
              <a
                href={company.whatsapp_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="hover:text-white transition-colors inline-flex items-center gap-1"
              >
                WhatsApp {company.phoneDisplay}
              </a>
            </li>
          )}
          {company.company_email && (
            <li>
              <a
                href={company.mailtoHref}
                data-testid="footer-email"
                className="hover:text-white transition-colors"
              >
                {company.company_email}
              </a>
            </li>
          )}
          {company.company_nit && (
            <li data-testid="footer-nit" className="text-[10px] text-slate-500">
              {company.company_legal_name || 'Automarket Colombia SAS'} · NIT {company.company_nit}
            </li>
          )}
        </ul>
        <div className="mt-4 flex items-center gap-2">
          {(company.socials.length ? company.socials : []).map((s) => (
            <a
              key={s.slug}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={s.label}
              data-testid={`footer-social-${s.slug}`}
              className={`h-9 w-9 inline-flex items-center justify-center bg-white/10 rounded-md transition-colors ${s.hover}`}
            >
              <SocialIcon slug={s.slug} />
            </a>
          ))}
        </div>
      </div>

      <div>
        <div className="font-bold uppercase text-[10px] tracking-[0.2em] text-orange-400">La empresa</div>
        <ul className="mt-3 space-y-2">
          <li><Link to="/quienes-somos" className="hover:text-white transition-colors">Quiénes somos</Link></li>
          <li><Link to="/ayuda" className="hover:text-white transition-colors">Centro de ayuda</Link></li>
          <li><Link to="/pqr" className="hover:text-white transition-colors">PQR</Link></li>
          <li><Link to="/contacto" className="hover:text-white transition-colors">Contacto</Link></li>
        </ul>
      </div>

      <div>
        <div className="font-bold uppercase text-[10px] tracking-[0.2em] text-orange-400">Comprar</div>
        <ul className="mt-3 space-y-2">
          <li><Link to="/catalogo" className="hover:text-white transition-colors">Catálogo</Link></li>
          <li><Link to="/ofertas" className="hover:text-white transition-colors">Ofertas</Link></li>
          <li><Link to="/mis-pedidos" className="hover:text-white transition-colors">Mis pedidos</Link></li>
          <li><Link to="/favoritos" className="hover:text-white transition-colors">Favoritos</Link></li>
        </ul>
      </div>

      <div>
        <div className="font-bold uppercase text-[10px] tracking-[0.2em] text-orange-400">Vender</div>
        <ul className="mt-3 space-y-2">
          <li><Link to="/registro?rol=seller" className="hover:text-white transition-colors">Ser vendedor</Link></li>
          <li><Link to="/vendedor" className="hover:text-white transition-colors">Panel del vendedor</Link></li>
          <li><Link to="/ayuda#vender" className="hover:text-white transition-colors">Guía para vender</Link></li>
        </ul>
      </div>

      <div>
        <div className="font-bold uppercase text-[10px] tracking-[0.2em] text-orange-400">Legal</div>
        <ul className="mt-3 space-y-2">
          <li><Link to="/terminos" className="hover:text-white transition-colors">Términos y condiciones</Link></li>
          <li><Link to="/privacidad" className="hover:text-white transition-colors">Política de privacidad</Link></li>
          <li><Link to="/politicas" className="hover:text-white transition-colors">Políticas de compra</Link></li>
          <li><Link to="/devoluciones" className="hover:text-white transition-colors">Devoluciones</Link></li>
          <li><Link to="/politica-de-envios" data-testid="footer-link-politica-envios" className="hover:text-white transition-colors">Tarifas, envíos y subsidios</Link></li>
        </ul>
      </div>
    </div>

    <div className="border-t border-slate-800 bg-slate-950">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-[11px] text-slate-400">
        <div>© 2026 {company.company_legal_name || 'AutoMarket Colombia'} · Todos los derechos reservados</div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-slate-500 uppercase font-semibold tracking-wider text-[10px]">Métodos de pago:</span>
          {PAYMENT_METHODS.map((m) => <PaymentBadge key={m.label} m={m} />)}
        </div>
      </div>
    </div>
  </footer>
  );
};

export const Layout = ({ children }) => (
  <div className="min-h-screen flex flex-col bg-white">
    <Header />
    <PersistentShortcutBar />
    <DashboardNav />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);
